/*
 * Copyright (c) 2023. Tooling Team
 */

import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Card } from "@uhg-abyss/web/ui/Card";
import { LAB_DOMAIN_VALUE, PATHS } from "../utils/constants";
import { RouteDisplay } from "../components/navigation/RouteDisplay";
import Layout from "../components/layout/Layout.js";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useCreateMapMutation } from "../app/services/mapsApi.js";
import { useGetClientListQuery } from "../app/services/clientApi.js";
import {
  selectSystemByDomain,
  useGetAllDomainQuery,
  selectDomainMap,
} from "../app/services/conceptsApi.js";
import { updateNewMapLocation } from "../app/slices/EIPCreateMap.slice.js";
import { getSelectedObject } from "../utils/funcs";
import { getClientDropdownValues } from "../utils/clientDropdown";

export default function EIPCreateMap() {
  const history = useHistory();
  const dispatch = useDispatch();
  const instructionText =
    "Input a Client, Domain, and Standard Code System to create a new map.";
  const [createMapApi] = useCreateMapMutation();
  const [selectedDomain, setSelectedDomain] = useState("");
  const [selectedSystem, setSelectedSystem] = useState("");
  const [client, setClient] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(true);
  useGetAllDomainQuery();
  const { data: clientListResponse } = useGetClientListQuery();
  const [placeholderMessage, setPlaceholderMessage] = useState("Loading...");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clientDropdownValues = useMemo(() => {
    return getClientDropdownValues(clientListResponse);
  }, [clientListResponse]);

  const systemDropdownValues = useSelector(
    selectSystemByDomain(selectedDomain)
  );
  const domainDropdownValues = useSelector(selectDomainMap);
  const setClientDropdown = (event) => {
    const selectedClient = getSelectedObject(event, clientDropdownValues);
    setClient(selectedClient);
  };

  const setDomainDropdown = (event) => {
    const nextDomain = getSelectedObject(event, domainDropdownValues);
    setSelectedDomain(nextDomain);
    setSelectedSystem(null);
  };

  const setSystemDropdown = (event) => {
    setSelectedSystem(getSelectedObject(event, systemDropdownValues));
  };

  useEffect(() => {
    if (selectedDomain && client && selectedSystem) {
      setButtonDisabled(false);
    } else {
      if (!buttonDisabled) {
        setButtonDisabled(true);
      }
    }
  }, [selectedDomain, client, selectedSystem]);

  useEffect(() => {
    if (domainDropdownValues.length > 0) {
      setPlaceholderMessage("Select...");
    }
  }, [domainDropdownValues]);

  const isDataLoading = () => {
    const isLoading = domainDropdownValues.length <= 0;
    return isLoading;
  };

  const createMap = async () => {
    setIsSubmitting(true);
    const requestBody = {
      sourceMapID: "",
      clientId: client ? client.label : null,
      conceptSetId: selectedSystem.value,
      module: selectedDomain?.label === LAB_DOMAIN_VALUE ? "lab" : "common",
    };

    try {
      await createMapApi(requestBody).unwrap();
      dispatch(
        updateNewMapLocation({
          client: client ? client.label : null,
          selectedDomain: selectedDomain ? selectedDomain.label : null,
          selectedSystem: selectedSystem ? selectedSystem.label : null,
        })
      );
      history.push(PATHS.editMapMapValueDefinition.url);
    } catch (err) {
      setIsSubmitting(false);
      let alertText =
        "There was a problem creating map for Client: " +
        (client ? client.label : "") +
        " Domain: " +
        selectedDomain.label +
        " and Standard Code System: " +
        selectedSystem.label +
        ". ";
      if (err.error) {
        alertText += err.error;
      }
      window.alert(alertText);
    }
  };

  return (
    <Layout>
      <div className="grid">
        <RouteDisplay
          pageRouteDisplay={[PATHS.mapManagement, PATHS.createMap]}
        />
        <div className="row mt-3 justify-content-center">
          <div className="col-12 col-md-8 col-lg-5 col-xl-4">
            <Card className="p-4">
              <h2 className="mb-3">Create New Map</h2>
              <h5 className="mb-3">{instructionText}</h5>
              <div className="mb-3">
                <label>Client</label>
                <SelectInput
                  placeholder={"Select..."}
                  isSearchable
                  value={client ? client.value : ""}
                  onChange={setClientDropdown}
                  options={clientDropdownValues}
                />
              </div>
              <div className="mb-3">
                <label>{"Domain"}</label>
                <SelectInput
                  isDisabled={isDataLoading()}
                  placeholder={placeholderMessage}
                  isSearchable
                  value={selectedDomain ? selectedDomain.value : ""}
                  onChange={setDomainDropdown}
                  options={domainDropdownValues}
                />
              </div>
              <div className="mb-3">
                <label>{"Standard Code System"}</label>
                <SelectInput
                  isDisabled={!selectedDomain}
                  placeholder={placeholderMessage}
                  isSearchable
                  value={selectedSystem ? selectedSystem.value : ""}
                  onChange={setSystemDropdown}
                  options={systemDropdownValues}
                />
              </div>
              <div className="d-flex gap-2">
                <Button
                  onClick={() => createMap()}
                  isDisabled={buttonDisabled}
                  isLoading={isSubmitting}
                  ariaLoadingLabel="Creating Map"
                >
                  Create New Map
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
